<script lang="ts">
	import { _ } from 'svelte-i18n';
	import { fade, fly, scale } from 'svelte/transition';
	import { onMount } from 'svelte';
	import Header from '$lib/components/sections/Header.svelte';
	import Footer from '$lib/components/sections/Footer.svelte';
	import SeoContent from '$lib/components/sections/SeoContent.svelte';
	import Button from '$lib/components/primitives/Button.svelte';
	import Icon from '$lib/components/primitives/Icon.svelte';

	let visible = $state(true); // SSR: start visible so hero + <h1> are in server HTML (Ahrefs: "H1 tag missing"). onMount re-set is a no-op; entrance animations still play on client-side navigation.

	onMount(() => {
		visible = true;
	});

	const challenges = [
		{
			person: $_('features.teamSupport.challenges.challenge1.person'),
			problem: $_('features.teamSupport.challenges.challenge1.problem'),
			solution: $_('features.teamSupport.challenges.challenge1.solution')
		},
		{
			person: $_('features.teamSupport.challenges.challenge2.person'),
			problem: $_('features.teamSupport.challenges.challenge2.problem'),
			solution: $_('features.teamSupport.challenges.challenge2.solution')
		},
		{
			person: $_('features.teamSupport.challenges.challenge3.person'),
			problem: $_('features.teamSupport.challenges.challenge3.problem'),
			solution: $_('features.teamSupport.challenges.challenge3.solution')
		}
	];

	const features = [
		{
			icon: 'users',
			title: $_('features.teamSupport.features.feature1.title'),
			description: $_('features.teamSupport.features.feature1.description')
		},
		{
			icon: 'calendar',
			title: $_('features.teamSupport.features.feature2.title'),
			description: $_('features.teamSupport.features.feature2.description')
		},
		{
			icon: 'shield',
			title: $_('features.teamSupport.features.feature3.title'),
			description: $_('features.teamSupport.features.feature3.description')
		},
		{
			icon: 'trending-up',
			title: $_('features.teamSupport.features.feature4.title'),
			description: $_('features.teamSupport.features.feature4.description')
		}
	];
</script>

<svelte:head>
	<title>{$_('features.teamSupport.meta.title')}</title>
	<meta name="description" content={$_('features.teamSupport.meta.description')} />
	<meta property="og:title" content="{$_('features.teamSupport.meta.title')}" />
	<meta property="og:description" content={$_('features.teamSupport.meta.description')} />
</svelte:head>

<Header />

<div class="feature-page">
	<!-- Hero Section -->
	<section class="hero">
		<div class="container">
			{#if visible}
				<div class="hero__grid">
					<div class="hero__content" in:fade={{ duration: 600, delay: 100 }}>
						<div class="hero__story" in:fly={{ y: 30, duration: 600, delay: 200 }}>
							<p class="hero__lead">{$_('features.teamSupport.hero.lead')}</p>
						</div>
						<h1 class="hero__title" in:fly={{ y: 30, duration: 600, delay: 300 }}>
							{@html $_('features.teamSupport.hero.title')}
						</h1>
						<p class="hero__subtitle" in:fly={{ y: 30, duration: 600, delay: 400 }}>
							{$_('features.teamSupport.hero.subtitle')}
						</p>
						<div class="hero__actions" in:fly={{ y: 30, duration: 600, delay: 500 }}>
							<Button variant="primary" size="lg" href="https://app.heyweek.com/?signup">
								{$_('features.teamSupport.hero.primaryCta')}
							</Button>
							<Button variant="ghost" size="lg" href="#challenges">
								{$_('features.teamSupport.hero.secondaryCta')}
							</Button>
						</div>
					</div>
					<div class="hero__visual" in:scale={{ duration: 800, delay: 400 }}>
						<div class="team-visual">
							<div class="team-member team-member--online">
								<div class="team-member__avatar"></div>
								<div class="team-member__status online"></div>
								<div class="team-member__work">Design Review</div>
							</div>
							<div class="team-member team-member--focused">
								<div class="team-member__avatar"></div>
								<div class="team-member__status focused"></div>
								<div class="team-member__work">Deep Work - API</div>
							</div>
							<div class="team-member team-member--away">
								<div class="team-member__avatar"></div>
								<div class="team-member__status away"></div>
								<div class="team-member__work">Lunch Break</div>
							</div>
							<div class="team-stats">
								<div class="team-stats__item">
									<div class="team-stats__number">3/5</div>
									<div class="team-stats__label">Online</div>
								</div>
								<div class="team-stats__item">
									<div class="team-stats__number">85%</div>
									<div class="team-stats__label">Capacity</div>
								</div>
							</div>
						</div>
					</div>
				</div>
			{/if}
		</div>
	</section>

	<!-- Challenges Section -->
	<section class="challenges" id="challenges">
		<div class="container">
			<div class="challenges__header">
				<h2 class="challenges__title">{$_('features.teamSupport.challenges.title')}</h2>
				<p class="challenges__subtitle">{$_('features.teamSupport.challenges.subtitle')}</p>
			</div>
			<div class="challenges__list">
				{#each challenges as challenge, index}
					<div class="challenge-item" class:reverse={index % 2 === 1}>
						<div class="challenge-item__problem">
							<div class="challenge-item__person">{challenge.person}</div>
							<blockquote class="challenge-item__quote">
								{challenge.problem}
							</blockquote>
						</div>
						<div class="challenge-item__arrow">
							<Icon name="arrow-right" size={24} />
						</div>
						<div class="challenge-item__solution">
							<div class="challenge-item__check">
								<Icon name="check" size={20} />
							</div>
							<p class="challenge-item__answer">{challenge.solution}</p>
						</div>
					</div>
				{/each}
			</div>
		</div>
	</section>

	<!-- Features Section -->
	<section class="features">
		<div class="container">
			<div class="features__header">
				<h2 class="features__title">{$_('features.teamSupport.features.title')}</h2>
			</div>
			<div class="features__grid">
				{#each features as feature, index}
					<div class="feature-showcase" class:reverse={index % 2 === 1}>
						<div class="feature-showcase__visual">
							<div class="feature-demo">
								<Icon name={feature.icon} size={48} style="color: rgb(255, 94, 126);" />
							</div>
						</div>
						<div class="feature-showcase__content">
							<h3 class="feature-showcase__title">{feature.title}</h3>
							<p class="feature-showcase__description">{feature.description}</p>
						</div>
					</div>
				{/each}
			</div>
		</div>
	</section>

	<!-- CTA Section -->
	<section class="cta">
		<div class="container">
			<div class="cta__content">
				<h2 class="cta__title">{$_('features.teamSupport.cta.title')}</h2>
				<p class="cta__subtitle">
					{$_('features.teamSupport.cta.subtitle')}
				</p>
				<div class="cta__actions">
					<Button variant="primary" size="lg" href="https://app.heyweek.com/?signup">
						{$_('features.teamSupport.cta.primaryButton')}
					</Button>
					<Button variant="ghost" size="lg" href="/pricing">
						{$_('features.teamSupport.cta.secondaryButton')}
					</Button>
				</div>
			</div>
		</div>
	</section>
</div>

<SeoContent slug="team-support" />

<Footer />

<style>
	.feature-page {
		min-height: 100vh;
	}

	/* Hero Section - Asymmetric grid layout */
	.hero {
		padding: var(--space-20) 0;
		background: linear-gradient(135deg, var(--bg-secondary) 0%, transparent 50%);
		overflow: hidden;
	}

	.hero__grid {
		display: grid;
		grid-template-columns: 1fr 1fr;
		gap: var(--space-16);
		align-items: center;
		max-width: 1200px;
		margin: 0 auto;
	}

	.hero__story {
		margin-bottom: var(--space-6);
	}

	.hero__lead {
		font-size: var(--font-size-lg);
		color: var(--text-secondary);
		font-weight: var(--font-weight-medium);
		font-style: italic;
	}

	.hero__title {
		font-size: var(--font-size-5xl);
		font-weight: var(--font-weight-bold);
		line-height: 1.1;
		color: var(--text-primary);
		margin-bottom: var(--space-6);
		letter-spacing: -0.02em;
	}

	.hero__subtitle {
		font-size: var(--font-size-lg);
		color: var(--text-secondary);
		line-height: var(--line-height-relaxed);
		margin-bottom: var(--space-8);
	}

	.hero__actions {
		display: flex;
		gap: var(--space-4);
		flex-wrap: wrap;
	}

	/* Team Visual */
	.team-visual {
		position: relative;
		padding: var(--space-8);
		background: var(--bg-primary);
		border: 1px solid var(--border-light);
		border-radius: var(--radius-xl);
		box-shadow: var(--shadow-xl);
	}

	.team-member {
		display: flex;
		align-items: center;
		gap: var(--space-3);
		margin-bottom: var(--space-4);
		padding: var(--space-3);
		background: var(--bg-secondary);
		border-radius: var(--radius-md);
		position: relative;
	}

	.team-member__avatar {
		width: 32px;
		height: 32px;
		background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
		border-radius: var(--radius-full);
		position: relative;
	}

	.team-member--focused .team-member__avatar {
		background: linear-gradient(135deg, #f093fb 0%, #f5576c 100%);
	}

	.team-member--away .team-member__avatar {
		background: linear-gradient(135deg, #4facfe 0%, #00f2fe 100%);
	}

	.team-member__status {
		position: absolute;
		bottom: -2px;
		right: -2px;
		width: 12px;
		height: 12px;
		border-radius: var(--radius-full);
		border: 2px solid var(--bg-primary);
	}

	.team-member__status.online {
		background: #10b981;
	}

	.team-member__status.focused {
		background: #f59e0b;
	}

	.team-member__status.away {
		background: #6b7280;
	}

	.team-member__work {
		font-size: var(--font-size-sm);
		color: var(--text-secondary);
		flex: 1;
	}

	.team-stats {
		display: flex;
		gap: var(--space-6);
		justify-content: center;
		margin-top: var(--space-6);
		padding-top: var(--space-4);
		border-top: 1px solid var(--border-light);
	}

	.team-stats__number {
		font-size: var(--font-size-xl);
		font-weight: var(--font-weight-bold);
		color: rgb(255, 94, 126);
		text-align: center;
	}

	.team-stats__label {
		font-size: var(--font-size-xs);
		color: var(--text-secondary);
		text-align: center;
		margin-top: var(--space-1);
	}

	/* Challenges Section */
	.challenges {
		padding: var(--space-20) 0;
	}

	.challenges__header {
		text-align: center;
		margin-bottom: var(--space-16);
	}

	.challenges__title {
		font-size: var(--font-size-3xl);
		font-weight: var(--font-weight-semibold);
		color: var(--text-primary);
		margin-bottom: var(--space-4);
		letter-spacing: -0.02em;
	}

	.challenges__subtitle {
		font-size: var(--font-size-lg);
		color: var(--text-secondary);
	}

	.challenges__list {
		max-width: 1100px;
		margin: 0 auto;
		display: flex;
		flex-direction: column;
		gap: var(--space-12);
	}

	.challenge-item {
		display: grid;
		grid-template-columns: 1fr auto 1fr;
		gap: var(--space-8);
		align-items: center;
	}

	.challenge-item.reverse {
		direction: rtl;
	}

	.challenge-item.reverse > * {
		direction: ltr;
	}

	.challenge-item__problem {
		padding: var(--space-6);
		background: var(--bg-secondary);
		border-radius: var(--radius-lg);
		border-left: 4px solid #ef4444;
	}

	.challenge-item__person {
		font-size: var(--font-size-sm);
		color: var(--text-secondary);
		text-transform: uppercase;
		letter-spacing: 0.05em;
		margin-bottom: var(--space-2);
	}

	.challenge-item__quote {
		font-size: var(--font-size-lg);
		color: var(--text-primary);
		font-style: italic;
		margin: 0;
		line-height: 1.4;
	}

	.challenge-item__arrow {
		color: rgb(255, 94, 126);
	}

	.challenge-item__solution {
		padding: var(--space-6);
		background: rgba(255, 94, 126, 0.05);
		border: 1px solid rgba(255, 94, 126, 0.2);
		border-radius: var(--radius-lg);
		position: relative;
	}

	.challenge-item__check {
		position: absolute;
		top: -12px;
		left: var(--space-6);
		width: 24px;
		height: 24px;
		background: rgb(255, 94, 126);
		color: white;
		border-radius: var(--radius-full);
		display: flex;
		align-items: center;
		justify-content: center;
	}

	.challenge-item__answer {
		font-size: var(--font-size-base);
		color: var(--text-primary);
		line-height: 1.6;
		margin: 0;
		margin-top: var(--space-2);
	}

	/* Features Section */
	.features {
		padding: var(--space-20) 0;
		background: var(--bg-secondary);
	}

	.features__header {
		text-align: center;
		margin-bottom: var(--space-16);
	}

	.features__title {
		font-size: var(--font-size-3xl);
		font-weight: var(--font-weight-semibold);
		color: var(--text-primary);
		letter-spacing: -0.02em;
	}

	.features__grid {
		max-width: 1100px;
		margin: 0 auto;
		display: flex;
		flex-direction: column;
		gap: var(--space-16);
	}

	.feature-showcase {
		display: grid;
		grid-template-columns: 1fr 2fr;
		gap: var(--space-12);
		align-items: center;
	}

	.feature-showcase.reverse {
		direction: rtl;
	}

	.feature-showcase.reverse > * {
		direction: ltr;
	}

	.feature-showcase__visual {
		display: flex;
		align-items: center;
		justify-content: center;
	}

	.feature-demo {
		width: 120px;
		height: 120px;
		background: var(--bg-primary);
		border: 1px solid var(--border-light);
		border-radius: var(--radius-xl);
		display: flex;
		align-items: center;
		justify-content: center;
		box-shadow: var(--shadow-lg);
	}

	.feature-showcase__title {
		font-size: var(--font-size-2xl);
		font-weight: var(--font-weight-semibold);
		color: var(--text-primary);
		margin-bottom: var(--space-4);
		letter-spacing: -0.02em;
	}

	.feature-showcase__description {
		font-size: var(--font-size-lg);
		color: var(--text-secondary);
		line-height: 1.6;
	}

	/* CTA Section */
	.cta {
		padding: var(--space-20) 0;
		text-align: center;
		background: linear-gradient(180deg, transparent 0%, var(--bg-secondary) 100%);
	}

	.cta__content {
		max-width: 800px;
		margin: 0 auto;
	}

	.cta__title {
		font-size: var(--font-size-3xl);
		font-weight: var(--font-weight-semibold);
		color: var(--text-primary);
		margin-bottom: var(--space-4);
		letter-spacing: -0.02em;
	}

	.cta__subtitle {
		font-size: var(--font-size-lg);
		color: var(--text-secondary);
		margin-bottom: var(--space-8);
		line-height: 1.6;
	}

	.cta__actions {
		display: flex;
		gap: var(--space-4);
		justify-content: center;
		flex-wrap: wrap;
	}

	/* Responsive */
	@media (max-width: 768px) {
		.hero__grid {
			grid-template-columns: 1fr;
			gap: var(--space-8);
		}

		.hero__title {
			font-size: 2rem;
		}

		.challenge-item {
			grid-template-columns: 1fr;
			gap: var(--space-4);
		}

		.challenge-item.reverse {
			direction: ltr;
		}

		.challenge-item__arrow {
			transform: rotate(90deg);
			justify-self: center;
		}

		.feature-showcase,
		.feature-showcase.reverse {
			grid-template-columns: 1fr;
			direction: ltr;
			text-align: center;
		}
	}
</style>
